/**
 * Toast Composable
 * Provides a reactive toast notification queue with convenience methods.
 */

import { reactive, readonly } from 'vue'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface ToastItem {
  id: number
  message: string
  type: ToastType
  duration: number
  createdAt: number
}

interface ToastOptions {
  duration?: number
}

const MAX_VISIBLE = 5

// Default auto-dismiss durations per type (ms)
const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 3000,
  error: 5000,
  warning: 4000,
  info: 3000,
}

let nextId = 0

// Shared reactive state
const state = reactive({
  queue: [] as ToastItem[],
})

function addToast(message: string, type: ToastType, options?: ToastOptions) {
  const duration = options?.duration ?? DEFAULT_DURATIONS[type]
  const toast: ToastItem = {
    id: nextId++,
    message,
    type,
    duration,
    createdAt: Date.now(),
  }

  state.queue.push(toast)

  // Keep queue bounded
  if (state.queue.length > MAX_VISIBLE) {
    state.queue.splice(0, state.queue.length - MAX_VISIBLE)
  }

  return toast.id
}

function removeToast(id: number) {
  const idx = state.queue.findIndex((t) => t.id === id)
  if (idx !== -1) {
    state.queue.splice(idx, 1)
  }
}

function clearAll() {
  state.queue.splice(0, state.queue.length)
}

/**
 * Composable for toast notifications.
 *
 * Usage:
 * ```ts
 * const toast = useToast()
 * toast.success('Project created!')
 * toast.error('Something went wrong', { duration: 6000 })
 * ```
 */
export function useToast() {
  return {
    /** Readonly reactive toast queue */
    queue: readonly(state).queue,

    success: (msg: string, opts?: ToastOptions) => addToast(msg, 'success', opts),
    error: (msg: string, opts?: ToastOptions) => addToast(msg, 'error', opts),
    warning: (msg: string, opts?: ToastOptions) => addToast(msg, 'warning', opts),
    info: (msg: string, opts?: ToastOptions) => addToast(msg, 'info', opts),

    remove: removeToast,
    clearAll,
  }
}

// Also export a singleton for non-setup contexts (e.g. router guards, api interceptors)
export const toast = {
  success: (msg: string, opts?: ToastOptions) => addToast(msg, 'success', opts),
  error: (msg: string, opts?: ToastOptions) => addToast(msg, 'error', opts),
  warning: (msg: string, opts?: ToastOptions) => addToast(msg, 'warning', opts),
  info: (msg: string, opts?: ToastOptions) => addToast(msg, 'info', opts),
  remove: removeToast,
  clearAll,
}
